<template>
  <div>
    <template
      v-for="(sectionData, sectionKey) in metaPolicyStruct"
      :key="sectionKey"
    >
      <!-- Only render if section has valid data -->
      <div
        v-if="shouldShowSection(sectionData)"
        class="bg-stone-100 p-4 rounded-lg mb-4"
      >
        <h2 class="text-base font-semibold leading-6 text-stone-700 mb-2">
          {{ formatTitle(sectionKey) }}
        </h2>
        <hr class="mb-4 border-gray-300" />
        <div class="text-gray-700">
          <!-- Handle Array Type Data -->
          <template v-if="Array.isArray(sectionData)">
            <div
              v-for="(item, index) in sectionData"
              :key="index"
              class="mb-4 last:mb-0 pb-4 last:pb-0 border-b last:border-0 border-gray-200"
            >
              <div
                v-for="(itemValue, itemKey) in item"
                :key="itemKey"
                class="mb-2"
              >
                <b>{{ formatTitle(itemKey) }}:</b>
                {{ formatValue(itemValue) }}
              </div>
            </div>
          </template>
          <!-- Handle Object Type Data -->
          <template v-else-if="isObject(sectionData)">
            <div
              v-for="(objValue, objKey) in sectionData"
              :key="objKey"
              class="mb-2"
            >
              <b>{{ formatTitle(objKey) }}:</b>
              {{ formatValue(objValue) }}
            </div>
          </template>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { defineProps } from "vue";

const props = defineProps({
  metaPolicyStruct: {
    type: Object,
    required: true,
  },
});

const isObject = (value) => {
  return value !== null && typeof value === "object" && !Array.isArray(value);
};

const shouldShowSection = (sectionData) => {
  if (Array.isArray(sectionData)) {
    return sectionData.length > 0;
  }
  if (isObject(sectionData)) {
    // Show object sections even if they contain 'unspecified' values
    return Object.keys(sectionData).length > 0;
  }
  return false;
};

const formatTitle = (key) => {
  return key
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const formatValue = (value) => {
  if (value === null || value === undefined) {
    return "N/A";
  }
  if (typeof value === "object" && !Array.isArray(value)) {
    // Handle nested objects
    const formattedObj = {};
    for (const [key, val] of Object.entries(value)) {
      formattedObj[formatTitle(key)] = formatValue(val);
    }
    return formattedObj;
  }
  if (value === "unspecified") {
    return "Not Specified";
  }
  if (value === "not_included") {
    return "Not Included";
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (typeof value === "string" && !isNaN(value.trim()) && value.trim() !== "") {
    const number = parseFloat(value);
    return number.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
  if (typeof value === "string" && value.includes("_")) {
    return value
      .split("_") // Split by underscore
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize each word
      .join(" "); // Join with spaces
  }
  return value;
};
</script>
